import { Checkbox, Paper, SimpleGrid, Stack, Textarea, TextInput } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useTranslation } from 'react-i18next'
import { IconUsers } from '@tabler/icons-react'
import type { RegisteredVisitor } from '../types'
import { inputStyles, textareaStyles } from '../styles'
import { SectionHeader } from './section-header'

interface PersonalInfoSectionProps {
  form: UseFormReturnType<RegisteredVisitor, (values: RegisteredVisitor) => RegisteredVisitor>
  isSmallDevice: boolean
  hasFoodIncluded: boolean
}

export function PersonalInfoSection({ form, isSmallDevice, hasFoodIncluded }: PersonalInfoSectionProps) {
  const { t } = useTranslation()

  return (
    <Paper p={isSmallDevice ? 'md' : 'xl'} radius="md" sx={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <SectionHeader icon={<IconUsers size={16} />} title={t('register.personal-info')} />

      <Stack spacing={12}>
        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={12}>
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.firstName')} placeholder={t('visitor.firstName')} {...form.getInputProps('firstName')} />
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.lastName')} placeholder={t('visitor.lastName')} {...form.getInputProps('lastName')} />
        </SimpleGrid>

        <TextInput withAsterisk sx={inputStyles} type="email" label={t('visitor.email')} placeholder={t('visitor.email')} {...form.getInputProps('email')} />

        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={12}>
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.company')} placeholder={t('visitor.company')} {...form.getInputProps('companyName')} />
          <TextInput sx={inputStyles} label={t('visitor.company-phone')} placeholder={t('visitor.company-phone')} {...form.getInputProps('companyPhone')} />
        </SimpleGrid>

        <TextInput withAsterisk sx={inputStyles} label={t('visitor.street')} placeholder={t('visitor.street')} {...form.getInputProps('address.street')} />

        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={12}>
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.city')} placeholder={t('visitor.city')} {...form.getInputProps('address.city')} />
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.province')} placeholder={t('visitor.province')} {...form.getInputProps('address.province')} />
        </SimpleGrid>

        <SimpleGrid cols={isSmallDevice ? 1 : 2} spacing={12}>
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.postal-code')} placeholder={t('visitor.postal-code')} {...form.getInputProps('address.postalCode')} />
          <TextInput withAsterisk sx={inputStyles} label={t('visitor.country')} placeholder={t('visitor.country')} {...form.getInputProps('address.country')} />
        </SimpleGrid>

        <TextInput sx={inputStyles} label={t('visitor.job-title')} placeholder={t('visitor.job-title')} {...form.getInputProps('jobTitle')} />

        {hasFoodIncluded && (
          <Textarea
            sx={textareaStyles}
            label={t('visitor.dietary-restrictions')}
            placeholder={t('visitor.dietary-restrictions')}
            minRows={2}
            {...form.getInputProps('dietaryRestrictions')}
          />
        )}

        <Checkbox
          mt={4}
          size="sm"
          color="red"
          styles={{
            input: { '&:checked': { background: '#850014', borderColor: '#850014' } },
            label: { fontSize: 13, color: '#4b5563' },
          }}
          label={t('visitor.consent')}
          {...form.getInputProps('hasConsent', { type: 'checkbox' })}
        />
      </Stack>
    </Paper>
  )
}
